import { useEffect, useState } from 'react';
import { convertTextToIntAiDecision } from '../../utils/formatters.ts';
import ReactSpeedometer from 'react-d3-speedometer';
import StockForecastChart from './StockForecastChart.tsx';
import dayjs from 'dayjs';
import RelativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(RelativeTime);

const MLSpeedometer = ({ base, technical, sample }) => {
	const [speedometerWidth, setSpeedometerWidth] = useState(window.innerWidth > 1000 ? 400 : 300);
	const [activeTab, setActiveTab] = useState(1);
	useEffect(() => {
		setTimeout(() => {
			// @ts-ignore
			HSStaticMethods.autoInit();
		}, 100);
	}, []);
	const theme = HSThemeAppearance.getAppearance();

	useEffect(() => {
		const tabs = document.querySelectorAll('button.ml-tab');
		const setActive = () => {
			setTimeout(() => {
				const activeTab = document.querySelector('button.ml-tab.active');
				setActiveTab(Number(activeTab?.getAttribute('data-hs-tab')?.replace('#tabs-with-card-', '')));
			}, 100);
		};

		tabs.forEach((tab) => {
			// Optionally, you can add event listeners if needed
			tab?.addEventListener('click', setActive);

			// Clean up event listener on unmount
			return () => {
				tab?.removeEventListener('click', setActive);
			};
		});
	}, []);

	window.addEventListener('resize', () => {
		const speedWidth = window.innerWidth > 1000 ? 400 : 300;
		setSpeedometerWidth(speedWidth);
	});

	useEffect(() => {
		if (location.hash === '#hourly') {
			setActiveTab(2);
			setTimeout(() => {
				document.getElementById('tabs-with-card-item-2')?.click();
			}, 100);
		}
	}, [location]);

	const getAccuracy = (str: string): string => {
		return `~${parseFloat(parseFloat(str).toFixed(3) * 100).toFixed(2)}%`;
	};

	return (
		<div className="max-w-full lg:max-w-[85rem] px-4 py-10 sm:px-6 lg:px-8 lg:py-14 mx-auto">
			<div className="relative p-6 md:p-16">
				<div className="relative lg:grid lg:grid-cols-12 lg:gap-16 lg:items-center">
					<div className="mb-10 lg:mb-0 lg:col-span-6 lg:col-start-8 lg:order-2">
						<h2 className="text-2xl text-gray-800 font-bold sm:text-3xl dark:text-neutral-200">
							Machine Learning {base.ticker} Prediction
						</h2>
						<h5 className="mt-1">Our ML model was trained using historical data and over 25 technical and other indicators.</h5>
						<nav className="grid gap-4 mt-5 md:mt-10" aria-label="Tabs" role="tablist">
							<button
								type="button"
								className="hs-tab-active:bg-white ml-tab hs-tab-active:shadow-md hs-tab-active:hover:border-transparent text-start hover:bg-gray-200 p-4 md:p-5 rounded-xl dark:hs-tab-active:bg-neutral-700 dark:hover:bg-neutral-700 active"
								id="tabs-with-card-item-1"
								data-hs-tab="#tabs-with-card-1"
								aria-controls="tabs-with-card-1"
								role="tab"
							>
								<span className="flex">
									<svg
										className="flex-shrink-0 mt-2 size-6 md:size-7 hs-tab-active:text-blue-600 text-gray-800 dark:hs-tab-active:text-blue-500 dark:text-neutral-200"
										xmlns="http://www.w3.org/2000/svg"
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="m12 14 4-4" />
										<path d="M3.34 19a10 10 0 1 1 17.32 0" />
									</svg>
									<span className="grow ms-6">
										<span className="block text-lg font-semibold hs-tab-active:text-blue-600 text-gray-800 dark:hs-tab-active:text-blue-500 dark:text-neutral-200">
											Daily {base.ticker} Prediction
										</span>
										<div className="block mt-1 text-gray-800 dark:hs-tab-active:text-gray-200 dark:text-neutral-200">
											<p>ML Accuracy: {getAccuracy(technical?.daily?.accuracy)}</p>
										</div>
									</span>
								</span>
							</button>

							<button
								type="button"
								className="hs-tab-active:bg-white ml-tab hs-tab-active:shadow-md hs-tab-active:hover:border-transparent text-start hover:bg-gray-200 p-4 md:p-5 rounded-xl dark:hs-tab-active:bg-neutral-700 dark:hover:bg-neutral-700"
								id="tabs-with-card-item-2"
								data-hs-tab="#tabs-with-card-2"
								aria-controls="tabs-with-card-2"
								role="tab"
							>
								<span className="flex">
									<svg
										className="flex-shrink-0 mt-2 size-6 md:size-7 hs-tab-active:text-blue-600 text-gray-800 dark:hs-tab-active:text-blue-500 dark:text-neutral-200"
										xmlns="http://www.w3.org/2000/svg"
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="m12 14 4-4" />
										<path d="M3.34 19a10 10 0 1 1 17.32 0" />
									</svg>
									<span className="grow ms-6">
										<span className="block text-lg font-semibold hs-tab-active:text-blue-600 text-gray-800 dark:hs-tab-active:text-blue-500 dark:text-neutral-200">
											Hourly {base.ticker} Prediction
										</span>
										<div className="block mt-1 text-gray-800 dark:hs-tab-active:text-gray-200 dark:text-neutral-200">
											<p>ML Accuracy: {getAccuracy(technical?.hourly.accuracy)}</p>
										</div>
									</span>
								</span>
							</button>
						</nav>
					</div>

					<div className="lg:col-span-6">
						<div className="relative">
							<div>
								<div
									id="tabs-with-card-1"
									className="flex justify-center md:justify-start"
									role="tabpanel"
									aria-labelledby="tabs-with-card-item-1"
								>
									<ReactSpeedometer
										width={speedometerWidth}
										needleHeightRatio={0.7}
										value={convertTextToIntAiDecision(technical.daily.ai_decision || '')}
										minValue={0}
										maxValue={3}
										customSegmentStops={[0, 1, 2, 3]}
										segmentColors={['#ef4444', 'rgba(103,106,122,0.99)', '#84cc16']}
										currentValueText={`as of ${dayjs(technical?.daily.timestamp).fromNow()}`}
										customSegmentLabels={[
											{
												text: 'SELL',
												position: 'OUTSIDE',
												color: theme === 'dark' ? '#64748b' : '#9ca3af',
											},
											{
												text: 'HOLD',
												position: 'OUTSIDE',
												color: theme === 'dark' ? '#64748b' : '#9ca3af',
											},
											{
												text: 'BUY',
												position: 'OUTSIDE',
												color: theme === 'dark' ? '#64748b' : '#9ca3af',
											},
										]}
										ringWidth={47}
										needleTransitionDuration={3333}
										needleTransition="easeElastic"
										needleColor={'#84cc16'}
										textColor={theme === 'dark' ? '#64748b' : '#9ca3af'}
										forceRender={true}
									/>
								</div>

								<div
									id="tabs-with-card-2"
									className="hidden flex justify-center md:justify-start"
									role="tabpanel"
									aria-labelledby="tabs-with-card-item-2"
								>
									<ReactSpeedometer
										width={speedometerWidth}
										needleHeightRatio={0.7}
										value={convertTextToIntAiDecision(technical.hourly.ai_decision || '')}
										minValue={0}
										maxValue={3}
										customSegmentStops={[0, 1, 2, 3]}
										segmentColors={['#ff1919', 'rgba(103,106,122,0.99)', '#84cc16']}
										currentValueText={`as of ${dayjs(technical?.hourly.timestamp).fromNow()}`}
										customSegmentLabels={[
											{
												text: 'SELL',
												position: 'OUTSIDE',
												color: theme === 'dark' ? '#64748b' : '#9ca3af',
											},
											{
												text: 'HOLD',
												position: 'OUTSIDE',
												color: theme === 'dark' ? '#64748b' : '#9ca3af',
											},
											{
												text: 'BUY',
												position: 'OUTSIDE',
												color: theme === 'dark' ? '#64748b' : '#9ca3af',
											},
										]}
										ringWidth={47}
										needleTransitionDuration={2500}
										needleTransition="easeElastic"
										needleColor={'#84cc16'}
										textColor={theme === 'dark' ? '#64748b' : '#9ca3af'}
										forceRender={true}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="grid">
				<h2 className="text-2xl text-center text-gray-800 mb-3 font-bold sm:text-3xl dark:text-neutral-200">
					Sample {activeTab === 1 ? 'Daily' : activeTab === 2 ? 'Hourly' : ''} {base.ticker} Ticker ML Dataset:
				</h2>
				{activeTab === 1 && (
					<div className="dataset daily">
						<StockForecastChart initialData={sample.daily} ticker={base.ticker} />
					</div>
				)}
				{activeTab === 2 && (
					<div className="dataset hourly">
						<StockForecastChart initialData={sample.hourly} ticker={base.ticker} />
					</div>
				)}
			</div>
		</div>
	);
};

export default MLSpeedometer;
